import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Video, VideoOff, Mic, MicOff, Phone, MessageSquare, 
  Settings, Maximize2, Layers, RotateCcw, Eye, 
  AlertTriangle, CheckCircle, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usePoseDetection3D } from '../hooks/usePoseDetection3D';
import AROverlay from '../components/AROverlay';
import api from '../services/api';
import { toast } from 'sonner';

const LiveSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showAROverlay, setShowAROverlay] = useState(true);
  
  const isCoach = user?.role === 'coach';
  const isAthlete = !isCoach;

  // Pose detection for athlete
  const {
    videoRef,
    canvasRef,
    isLoading: poseLoading,
    error: poseError,
    landmarks2D,
    angles,
    cameraActive,
    startCamera,
    stopCamera,
    isRecording,
    recordedFrames,
    startRecording,
    stopRecording
  } = usePoseDetection3D(isAthlete);

  // State for receiving athlete data (coach side)
  const [athleteAngles, setAthleteAngles] = useState(null);
  const [athleteRecordedFrames, setAthleteRecordedFrames] = useState([]);

  const videoDimensions = useRef({ width: 1280, height: 720 });

  useEffect(() => {
    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (videoRef.current && cameraActive) {
      videoDimensions.current = {
        width: videoRef.current.videoWidth || 1280,
        height: videoRef.current.videoHeight || 720
      };
    }
  }, [cameraActive, videoRef]);

  // Send pose data to coach (simulated - in production would use WebRTC/WebSocket)
  useEffect(() => {
    if (isAthlete && session?.status === 'live') {
      // In production: send pose data via WebSocket/WebRTC
      const interval = setInterval(() => {
        if (angles) {
          api.post(`/live-sessions/${sessionId}/pose-data`, {
            angles
          }).catch(console.error);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isAthlete, angles, session, sessionId]);

  const loadSession = async () => {
    try {
      const res = await api.get(`/live-sessions/${sessionId}`);
      setSession(res.data);
      setLoading(false);
    } catch (err) {
      toast.error('Failed to load session');
      navigate('/live-sessions');
    }
  };

  const handleJoinSession = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/join`);
      if (isAthlete) {
        await startCamera();
        startRecording();
      }
      setIsConnected(true);
      toast.success('Joined session!');
      loadSession();
    } catch (err) {
      toast.error('Failed to join session');
    }
  };

  const handleStartSession = async () => {
    try {
      await api.post(`/live-sessions/${sessionId}/start`);
      setIsConnected(true);
      toast.success('Session started!');
      loadSession();
    } catch (err) {
      toast.error('Failed to start session');
    }
  };

  const handleEndSession = async () => {
    try {
      if (isAthlete && isRecording) {
        stopRecording();
        stopCamera();
      }
      await api.post(`/live-sessions/${sessionId}/end`);
      toast.success('Session ended');
      navigate('/live-sessions');
    } catch (err) {
      toast.error('Failed to end session');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <p className="text-zinc-400">Session not found</p>
          <Button className="mt-4" onClick={() => navigate('/live-sessions')}>
            Back to Sessions
          </Button>
        </div>
      </div>
    );
  }

  // Pre-session waiting room
  if (!isConnected && session.status !== 'live') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="hpme-card p-8 text-center">
            <div className="hpme-card-corner" />
            
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Video className="w-10 h-10 text-primary" />
            </div>
            
            <h1 className="font-secondary font-bold text-2xl mb-2">LIVE SESSION</h1>
            <p className="text-zinc-400 mb-6">
              {isCoach ? `Training session with ${session.athlete_name}` : `Session with Coach ${session.coach_name}`}
            </p>
            
            <div className="bg-zinc-800/50 p-4 rounded-sm mb-6">
              <p className="text-xs text-zinc-500 uppercase mb-2">Room Code</p>
              <p className="font-mono text-2xl text-primary tracking-widest">{session.room_code}</p>
            </div>

            <div className="text-sm text-zinc-500 mb-6">
              <p>Status: <span className="text-yellow-500 capitalize">{session.status}</span></p>
              {session.scheduled_at && (
                <p>Scheduled: {new Date(session.scheduled_at).toLocaleString()}</p>
              )}
            </div>

            {isCoach ? (
              <Button className="hpme-btn-primary w-full" onClick={handleStartSession}>
                Start Session
              </Button>
            ) : (
              <Button className="hpme-btn-primary w-full" onClick={handleJoinSession}>
                Join Session
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              className="w-full mt-3 text-zinc-400"
              onClick={() => navigate('/live-sessions')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active session view
  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Header */}
      <div className="h-14 bg-surface border-b border-zinc-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="font-secondary font-bold text-sm">LIVE</span>
          <span className="text-zinc-500">•</span>
          <span className="text-sm text-zinc-400">
            {isCoach ? session.athlete_name : `Coach ${session.coach_name}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 font-mono">{session.room_code}</span>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={handleEndSession}
          >
            <Phone className="w-4 h-4 mr-1" />
            End
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video/Pose Area */}
        <div className="flex-1 p-4">
          <div className="h-full flex flex-col">
            {/* Main Video Feed */}
            <div className="flex-1 relative bg-black rounded-sm overflow-hidden">
              {isAthlete ? (
                <>
                  {/* Athlete sees their own camera */}
                  <video 
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                    playsInline
                    muted
                  />
                  <canvas 
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  
                  {/* AR Overlay */}
                  {showAROverlay && landmarks2D && (
                    <AROverlay
                      landmarks2D={landmarks2D}
                      angles={angles}
                      exerciseName="Squat"
                      canvasWidth={videoDimensions.current.width}
                      canvasHeight={videoDimensions.current.height}
                      showGhostPose={true}
                      showColorSkeleton={true}
                      showGuides={true}
                    />
                  )}

                  {/* Recording indicator */}
                  {isRecording && (
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500/90 px-3 py-1 rounded-sm">
                      <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      <span className="text-white text-xs font-bold">REC</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Coach sees athlete's feed - Placeholder for WebRTC */}
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
                    <div className="text-center">
                      <Users className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
                      <p className="text-zinc-500">Waiting for athlete's video feed...</p>
                      <p className="text-xs text-zinc-500 mt-2">In production, WebRTC would stream here</p>
                    </div>
                  </div>
                </>
              )}

              {/* Metrics Overlay */}
              {angles && (
                <div className="absolute bottom-4 left-4 right-4 flex justify-between">
                  <div className="bg-zinc-900/80 backdrop-blur px-4 py-2 rounded-sm">
                    <div className="flex gap-6 text-xs">
                      <div>
                        <span className="text-zinc-500">L Knee:</span>
                        <span className="text-primary ml-1 font-mono">{angles.leftKnee?.toFixed(0) || '--'}°</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">R Knee:</span>
                        <span className="text-primary ml-1 font-mono">{angles.rightKnee?.toFixed(0) || '--'}°</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Symmetry:</span>
                        <span className="text-accent-blue ml-1 font-mono">{angles.kneeSymmetry?.toFixed(0) || '--'}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Toggle buttons */}
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={() => setShowAROverlay(!showAROverlay)}
                  className={`p-2 rounded-sm ${showAROverlay ? 'bg-primary text-black' : 'bg-zinc-800/80 text-zinc-400'}`}
                  title="Toggle AR Overlay"
                >
                  <Layers className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Side Panel - Chat/Notes */}
        {showChat && (
          <div className="w-80 border-l border-zinc-800 bg-surface flex flex-col">
            <div className="p-4 border-b border-zinc-800">
              <h3 className="font-secondary font-bold">SESSION CHAT</h3>
            </div>
            <div className="flex-1 p-4">
              <p className="text-zinc-500 text-sm text-center">Chat messages will appear here</p>
            </div>
            <div className="p-4 border-t border-zinc-800">
              <input 
                type="text" 
                placeholder="Type a message..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="h-20 bg-surface border-t border-zinc-800 flex items-center justify-center gap-4">
        <Button
          variant="ghost"
          size="lg"
          className={`rounded-full w-14 h-14 ${isMuted ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-white'}`}
          onClick={() => setIsMuted(!isMuted)}
        >
          {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </Button>
        
        <Button
          variant="ghost"
          size="lg"
          className={`rounded-full w-14 h-14 ${!isVideoOn ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-white'}`}
          onClick={() => {
            setIsVideoOn(!isVideoOn);
            if (isVideoOn) stopCamera();
            else startCamera();
          }}
        >
          {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
        </Button>
        
        <Button
          size="lg"
          className="rounded-full w-14 h-14 bg-red-500 hover:bg-red-600 text-white"
          onClick={handleEndSession}
        >
          <Phone className="w-6 h-6" />
        </Button>
        
        <Button
          variant="ghost"
          size="lg"
          className={`rounded-full w-14 h-14 ${showChat ? 'bg-primary/20 text-primary' : 'bg-zinc-800 text-white'}`}
          onClick={() => setShowChat(!showChat)}
        >
          <MessageSquare className="w-6 h-6" />
        </Button>
        
        <Button
          variant="ghost"
          size="lg"
          className="rounded-full w-14 h-14 bg-zinc-800 text-white"
        >
          <Settings className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
};

export default LiveSession;
